import React from 'react';
import { Check, Minus } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * WizardSteps — accessible multi-step progress indicator.
 *
 * Props:
 *   steps     — Array<{ id: string|number, label: string }>
 *   current   — number (0-based index of active step)
 *   skipped   — Set<number> — indices that were skipped
 *   completed — Set<number> — indices that are completed
 *
 * Color-only forbidden (WCAG AA): each state uses icon + color.
 *   completed → Check icon, green
 *   active    → number badge, sky-600
 *   skipped   → Minus icon, amber
 *   pending   → number badge, gray
 */
export default function WizardSteps({ steps = [], current = 0, skipped = new Set(), completed = new Set() }) {
  return (
    <nav
      aria-label="Wizard progress"
      className="flex items-center gap-0 w-full"
    >
      {steps.map((step, idx) => {
        const isCompleted = completed.has(idx);
        const isActive = idx === current;
        const isSkipped = skipped.has(idx) && !isCompleted && !isActive;

        let iconEl;
        let labelClass;

        if (isCompleted) {
          iconEl = (
            <div
              className="w-8 h-8 rounded-full bg-emerald-500 border-2 border-emerald-600 flex items-center justify-center flex-shrink-0 shadow-[0_2px_0_#059669]"
              aria-hidden="true"
            >
              <Check size={14} className="text-white" strokeWidth={2.5} />
            </div>
          );
          labelClass = 'text-emerald-700 font-bold';
        } else if (isActive) {
          iconEl = (
            <div
              className="w-8 h-8 rounded-full bg-sky-600 border-2 border-sky-700 flex items-center justify-center flex-shrink-0 shadow-[0_2px_0_#0369a1]"
              aria-hidden="true"
            >
              <span className="text-xs font-black text-white">{idx + 1}</span>
            </div>
          );
          labelClass = 'text-sky-700 font-black';
        } else if (isSkipped) {
          iconEl = (
            <div
              className="w-8 h-8 rounded-full bg-amber-100 border-2 border-amber-400 flex items-center justify-center flex-shrink-0"
              aria-hidden="true"
            >
              <Minus size={14} className="text-amber-600" strokeWidth={2.5} />
            </div>
          );
          labelClass = 'text-amber-600 font-semibold';
        } else {
          iconEl = (
            <div
              className="w-8 h-8 rounded-full bg-gray-100 border-2 border-gray-300 flex items-center justify-center flex-shrink-0"
              aria-hidden="true"
            >
              <span className="text-xs font-bold text-gray-500">{idx + 1}</span>
            </div>
          );
          labelClass = 'text-gray-400 font-medium';
        }

        const stepLabel = isCompleted
          ? 'completed'
          : isActive
          ? 'current'
          : isSkipped
          ? 'skipped'
          : 'pending';

        return (
          <React.Fragment key={step.id}>
            <div
              className="flex flex-col items-center gap-1 flex-shrink-0"
              aria-current={isActive ? 'step' : undefined}
            >
              <span className="sr-only">
                {step.label} — {stepLabel}
              </span>
              {iconEl}
              <span
                className={cn('text-[10px] leading-tight text-center hidden sm:block max-w-[60px] truncate', labelClass)}
                aria-hidden="true"
              >
                {step.label}
              </span>
            </div>
            {/* Connector line between steps */}
            {idx < steps.length - 1 && (
              <div
                className={cn(
                  'flex-1 h-0.5 mx-1 rounded-full border-t-2',
                  isCompleted ? 'border-emerald-400' : 'border-gray-200'
                )}
                aria-hidden="true"
              />
            )}
          </React.Fragment>
        );
      })}
    </nav>
  );
}
